/**
 * Dashboard view (Req 8.5, 8.6, 9.7).
 *
 * Calls `GET /api/dashboard` and renders, per crop (corn, soybeans), the current
 * Breakeven Price, current Cash Bid, and current revenue. Any value that failed
 * to load is shown with a per-value "unavailable" indicator while every value
 * that did load is still displayed — a partial failure never blanks the view
 * (Req 8.6). The aggregate call keeps the initial render fast (Req 8.5).
 */
import type { ApiClient } from "../api/client";
import { CROPS, cropLabel, type Crop } from "../config";
import type { CropDashboard, DashboardField } from "../api/types";
import { el, render } from "../ui/dom";
import { formatPerBushel4, formatPrice2, formatUsd, formatUtc, sourceLabel } from "../ui/format";

/** Build the dashboard view; loads asynchronously and fills in when ready. */
export function dashboardView(api: ApiClient): HTMLElement {
  const body = el("div", { class: "dashboard-body" }, [
    el("p", { class: "muted", text: "Loading dashboard…" }),
  ]);

  const root = el("section", { class: "view dashboard-view" }, [
    el("h2", { class: "view-title", text: "Dashboard" }),
    body,
  ]);

  void load();

  async function load(): Promise<void> {
    const result = await api.getDashboard();
    if (!result.ok) {
      // A whole-response failure still shows every crop as unavailable rather
      // than a blank screen (Req 8.6); the per-value loaders remain graceful.
      render(
        body,
        el("p", { class: "form-message is-error", attrs: { role: "alert" } }, [
          "Could not load the dashboard right now.",
        ]),
        el("div", { class: "crop-grid" }, CROPS.map((crop) => cropCard(fallbackCrop(crop))))
      );
      return;
    }

    const byCrop = new Map<Crop, CropDashboard>();
    for (const c of result.data.crops) {
      byCrop.set(c.crop, c);
    }
    render(
      body,
      el(
        "div",
        { class: "crop-grid" },
        CROPS.map((crop) => cropCard(byCrop.get(crop) ?? fallbackCrop(crop)))
      )
    );
  }

  return root;
}

/** An all-unavailable crop card used when the aggregate call itself failed. */
function fallbackCrop(crop: Crop): CropDashboard {
  return {
    crop,
    breakeven: { available: false },
    cashBid: { available: false },
    revenue: { available: false },
    unavailable: ["breakeven", "cashBid", "revenue"],
  };
}

/** Render one crop's card with its three values. */
function cropCard(data: CropDashboard): HTMLElement {
  return el("article", { class: "card crop-card" }, [
    el("h3", { class: "crop-name", text: cropLabel(data.crop) }),
    valueRow("Breakeven", data.breakeven, renderBreakeven),
    valueRow("Cash bid", data.cashBid, renderCashBid),
    valueRow("Revenue", data.revenue, renderRevenue),
  ]);
}

/** A labeled value row that shows the value or a per-value unavailable badge. */
function valueRow(
  label: string,
  field: DashboardField,
  renderValue: (value: unknown) => HTMLElement
): HTMLElement {
  return el("div", { class: "value-row" }, [
    el("span", { class: "value-label", text: label }),
    field.available
      ? renderValue(field.value)
      : el("span", {
          class: "value unavailable-badge",
          text: "Unavailable",
          attrs: { role: "status", "aria-label": `${label} unavailable` },
        }),
  ]);
}

/** Render the breakeven value (a computable BreakevenResult). */
function renderBreakeven(value: unknown): HTMLElement {
  const v = value as { breakevenPricePerBushel?: number };
  const price = typeof v.breakevenPricePerBushel === "number" ? v.breakevenPricePerBushel : NaN;
  return el("span", { class: "value", text: `${formatPerBushel4(price)}/bu` });
}

/** Render the cash-bid value (an available PriceView) with source + UTC + stale. */
function renderCashBid(value: unknown): HTMLElement {
  const v = value as {
    price?: number;
    source?: "acoop" | "manual" | "usda_ams";
    timestampUtc?: string;
    stale?: boolean;
  };
  const price = typeof v.price === "number" ? v.price : NaN;
  const children: (HTMLElement | string)[] = [
    el("span", { class: "value", text: `${formatPrice2(price)}/bu` }),
  ];
  const meta: string[] = [];
  if (v.source !== undefined) meta.push(sourceLabel(v.source));
  if (v.timestampUtc !== undefined) meta.push(formatUtc(v.timestampUtc));
  if (meta.length > 0) {
    children.push(el("span", { class: "value-meta", text: meta.join(" · ") }));
  }
  if (v.stale === true) {
    children.push(el("span", { class: "stale-badge", text: "Not current" }));
  }
  return el("div", { class: "value-stack" }, children);
}

/** Render the revenue value (a computable RevenueResult). */
function renderRevenue(value: unknown): HTMLElement {
  const v = value as {
    perBushelRevenue?: number;
    wholeFarmRevenue?: number;
    wholeFarmProfit?: number;
  };
  const children: (HTMLElement | string)[] = [];
  if (typeof v.perBushelRevenue === "number") {
    children.push(
      el("span", { class: "value", text: `${formatUsd(v.perBushelRevenue)}/bu` })
    );
  }
  if (typeof v.wholeFarmRevenue === "number") {
    children.push(
      el("span", { class: "value-meta", text: `Whole farm ${formatUsd(v.wholeFarmRevenue)}` })
    );
  }
  if (typeof v.wholeFarmProfit === "number") {
    children.push(
      el("span", {
        class: v.wholeFarmProfit < 0 ? "value-meta profit-negative" : "value-meta profit-positive",
        text: `Profit ${formatUsd(v.wholeFarmProfit)}`,
      })
    );
  }
  return el("div", { class: "value-stack" }, children);
}
